import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronsUpDown, Plus, Search, Check, Settings } from 'lucide-react';
import { listProjects, getProject } from '../../projects/lib/projects';
import { useTeam } from '../contexts/TeamContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import type { Project } from '../../../shared/lib/supabase';
import type { TeamWithMembers } from '../lib/teams';

export function TeamProjectNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTeam, userTeams, switchTeam } = useTeam();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingCurrentProject, setLoadingCurrentProject] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const projectButtonRef = useRef<HTMLButtonElement>(null);
  const teamButtonRef = useRef<HTMLButtonElement>(null);

  // Extract project ID from URL
  const projectMatch = location.pathname.match(/^\/projects\/([^\/]+)/);
  const projectId = projectMatch?.[1];

  // Load current project when projectId changes
  useEffect(() => {
    const loadCurrentProject = async () => {
      if (projectId) {
        setLoadingCurrentProject(true);
        
        // Check sessionStorage for cached project data
        const cachedProject = sessionStorage.getItem(`project_${projectId}`);
        if (cachedProject) {
          try {
            const parsedProject = JSON.parse(cachedProject);
            setCurrentProject(parsedProject);
          } catch (e) {
            console.error('Error parsing cached project:', e);
          }
        }
        
        try {
          const project = await getProject(projectId);
          setCurrentProject(project);
          // Cache the project data
          sessionStorage.setItem(`project_${projectId}`, JSON.stringify(project));
        } catch (error) {
          console.error('Error loading current project:', error);
          setCurrentProject(null);
        } finally {
          setLoadingCurrentProject(false);
        }
      } else {
        setCurrentProject(null);
        setLoadingCurrentProject(false);
      }
    };

    loadCurrentProject();
  }, [projectId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && 
          !dropdownRef.current.contains(event.target as Node) &&
          !projectButtonRef.current?.contains(event.target as Node) &&
          !teamButtonRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setShowTeamSelector(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Load current team's projects when dropdown opens
  useEffect(() => {
    if (isOpen && currentTeam) {
      loadProjects();
    }
  }, [isOpen, currentTeam?.id]);

  const loadProjects = async () => {
    if (!currentTeam || loadingProjects) return;
    
    setLoadingProjects(true);
    try {
      const projectList = await listProjects(currentTeam.id);
      setProjects(projectList);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleTeamSwitch = async (teamId: string) => {
    await switchTeam(teamId);
    setIsOpen(false);
    setShowTeamSelector(false);
    setTeamSearchQuery('');
    setProjectSearchQuery('');
    navigate('/');
  };

  const handleProjectClick = (project: Project) => {
    setIsOpen(false);
    setProjectSearchQuery('');
    // Cache the project data before navigating
    sessionStorage.setItem(`project_${project.id}`, JSON.stringify(project));
    navigate(`/projects/${project.id}`);
  };

  const handleCreateProject = () => {
    setIsOpen(false);
    // Navigate to home page and trigger create project modal
    navigate('/');
    setTimeout(() => {
      const event = new CustomEvent('openCreateProjectModal');
      window.dispatchEvent(event);
    }, 100);
  };

  const getFilteredProjects = () => {
    if (!projectSearchQuery) return projects;
    return projects.filter(project =>
      project.name.toLowerCase().includes(projectSearchQuery.toLowerCase())
    );
  };

  const getFilteredTeams = () => {
    if (!teamSearchQuery) return userTeams;
    return userTeams.filter(team =>
      team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())
    );
  };

  if (!currentTeam || !user) {
    return null;
  }

  return (
    <div className="relative flex items-center">
      {/* Separator */}
      <span className="mx-2 text-gray-400 text-xl font-extralight">/</span>
      
      {/* Team container */}
      <div className="flex items-center">
        {/* Team button - clickable to go to landing page */}
        <button
          onClick={() => {
            navigate('/');
          }}
          className="flex items-center rounded-md px-3 py-1.5 hover:opacity-80 transition-all cursor-pointer"
        >
          {/* Team Avatar */}
          {currentTeam.avatar_url ? (
            <img 
              src={currentTeam.avatar_url} 
              alt={currentTeam.name}
              className="w-8 h-8 rounded object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded bg-gray-700 flex items-center justify-center text-white font-bold text-sm">
              {currentTeam.name.charAt(0).toUpperCase()}
            </div>
          )}
          
          {/* Team Name */}
          <span className="ml-2 text-xl font-medium text-gray-900">
            {currentTeam.name}
          </span>
        </button>
        
        {/* Dropdown arrow - separate button */}
        <button
          ref={teamButtonRef}
          onClick={() => {
            setIsOpen(!isOpen);
            setShowTeamSelector(true);
          }}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          title="Select team"
        >
          <ChevronsUpDown className="w-4 h-4 text-gray-400 hover:text-gray-700 transition-colors" />
        </button>
      </div>
      
      {/* Separator */}
      <span className="mx-2 text-gray-400 text-xl font-extralight">/</span>
      
      {/* Project button container */}
      <div className="relative flex items-center">
        {/* Project Name - clickable to go to homepage */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (currentProject) {
              const projectHomePath = `/projects/${currentProject.id}`;
              // Navigate to project home
              navigate(projectHomePath);
              // Dispatch event to switch to home tab
              setTimeout(() => {
                const event = new CustomEvent('switchToHomeTab', { detail: { projectId: currentProject.id } });
                window.dispatchEvent(event);
              }, 100);
            }
          }}
          className="text-xl font-medium text-gray-900 hover:text-gray-700 transition-colors px-3 py-1.5 cursor-pointer"
          disabled={!currentProject || loadingCurrentProject}
        >
          {loadingCurrentProject && !currentProject ? (
            <span className="inline-block w-24">&nbsp;</span>
          ) : (
            currentProject?.name || 'Select Project'
          )}
        </button>
        
        {/* Dropdown arrow - separate button */}
        <button
          ref={projectButtonRef}
          onClick={() => {
            setIsOpen(!isOpen);
            setShowTeamSelector(false);
          }}
          className="p-1.5 rounded hover:bg-gray-100 transition-colors"
          title="Select project"
        >
          <ChevronsUpDown className="w-4 h-4 text-gray-400 hover:text-gray-700 transition-colors" />
        </button>

        {/* Project Dropdown */}
        {isOpen && !showTeamSelector && (
          <div ref={dropdownRef} className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-sm shadow-lg z-50 overflow-hidden">
            {/* Project Search */}
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Find Project in ${currentTeam.name}...`}
                  value={projectSearchQuery}
                  onChange={(e) => setProjectSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border-0 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                />
              </div>
            </div>
            
            {/* Projects Section */}
            <div className="px-3 pb-3">
              {!projectSearchQuery && (
                <div className="text-xs text-gray-500 px-1 pb-2">Projects</div>
              )}
              
              {/* Projects List */}
              <div className="max-h-[300px] overflow-y-auto">
                {loadingProjects ? (
                  <div className="py-8 text-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mx-auto"></div>
                  </div>
                ) : getFilteredProjects().length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500">
                    {projectSearchQuery ? 'No projects found' : 'No projects yet'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {getFilteredProjects().map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleProjectClick(project)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-md hover:bg-gray-50 transition-colors ${
                          currentProject?.id === project.id ? 'bg-gray-50' : ''
                        }`}
                      >
                        <span className="text-gray-900 truncate font-medium">{project.name}</span>
                        {currentProject?.id === project.id && (
                          <Check className="w-4 h-4 text-gray-600 flex-shrink-0 ml-2" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Create Project */}
              <button
                onClick={handleCreateProject}
                className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Project
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dropdown Menu */}
      {isOpen && showTeamSelector && (
        <div ref={dropdownRef} className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-sm shadow-lg z-50 overflow-hidden">
          {/* Team Search */}
          <div className="p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Find Team..."
                value={teamSearchQuery}
                onChange={(e) => setTeamSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm border-0 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                autoFocus
              />
            </div>
          </div>
          
          {/* Teams Label */}
          <div className="px-3 pb-1">
            <div className="text-xs text-gray-500">Teams</div>
          </div>
          
          {/* Teams List */}
          <div className="px-3 pb-3">
            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {getFilteredTeams().map((team) => (
                <div
                  key={team.id}
                  className={`w-full flex items-center px-3 py-2 rounded-md hover:bg-gray-50 transition-colors ${
                    team.id === currentTeam.id ? 'bg-gray-50' : ''
                  }`}
                >
                  <button
                    onClick={() => handleTeamSwitch(team.id)}
                    className="flex-1 flex items-center gap-3 text-left"
                  >
                    {team.avatar_url ? (
                      <img 
                        src={team.avatar_url} 
                        alt={team.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center text-white font-medium text-sm">
                        {team.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-sm text-gray-900 flex-1 text-left truncate font-medium">
                      {team.name}
                    </span>
                    {team.id === currentTeam.id && (
                      <Check className="w-4 h-4 text-gray-600 mr-2" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(false);
                      navigate(`/team-settings/${team.id}`);
                    }}
                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                    title="Team settings"
                  >
                    <Settings className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Create Team Option */}
            <button
              onClick={() => {
                setIsOpen(false);
                // Dispatch event to open create team modal
                const event = new CustomEvent('openCreateTeamModal');
                window.dispatchEvent(event);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Team
            </button>
          </div>
        </div>
      )}
      
    </div>
  );
}